import type { EmailDto, NewUserDto } from '@packages/shared/core/auth/services/dto';
import { FlowName } from '@packages/shared/core/quiz/entities';
import { ZodiacTypes } from '@packages/shared/core/zodiac/entities';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { AccountExistError } from '@packages/shared/core/common/errors';
import { AppThunk } from '@packages/shared/core/common/store';
import { generateUuid } from '@packages/shared/core/common/utils/generateUuid';
import { requireEmailConsentFeature } from '@packages/shared/core/feature-flags/config';
import { LoadingStatuses } from '@packages/shared/core/common/entities';
import { DEFAULT_BIRTH_DATE } from '@packages/shared/core/common/constants';
import { todayInMilliseconds } from '@packages/shared/core/common/utils';
import { AuthData } from '@packages/shared/core/auth/entities';
import { parseSearchParamsFromUrl } from '@packages/shared/core/common/utils/url/parseSearchParamsFromUrl';
import { getUtmTags } from '../selectors';
import { getUserInteraction } from '../userInteraction/selectors';
import { getUserPreferences, getZodiac } from './selectors';
import {
  requestPasswordResetSucceed,
  requestPasswordResetPending,
  signInFailed,
  signInSucceed,
  signInPending,
  signUpViaFacebookFailed,
  signUpViaFacebookSucceed,
  signUpViaFacebookPending,
  signUpViaGoogleFailed,
  signUpViaGoogleSucceed,
  signUpViaGooglePending,
  signUpViaQuizFailed,
  signUpViaQuizSucceed,
  signUpViaQuizPending,
  updateEmailSucceed,
  updateEmailPending,
  requestPasswordResetFailed,
  updateEmailFailed,
} from './actions';

export const signUpViaGoogle =
  ({ token, quizName, email }: { token: string; quizName: FlowName; email: string }): AppThunk<Promise<void>> =>
  async (dispatch, getState, extra) => {
    const {
      authService,
      analyticsService,
      sessionRecorder,
      crossDomainStorage,
      clarityHeatmapRecordingService,
      featureFlags,
      metaService,
    } = extra;

    const state = getState();
    const userPreferences = getUserPreferences(state);
    const zodiac = getZodiac(state);
    const utmTags = getUtmTags(state);
    const userInteraction = getUserInteraction(state);
    const attributionParams = analyticsService.getAttributionParameters();

    const sessionId = await analyticsService.getSessionId();
    const deviceId = await analyticsService.getDeviceId();
    const isOptOutSellUserData = crossDomainStorage.isOptOutSellUserData();

    const isEmailConsentRequired = featureFlags.isFeatureOn(requireEmailConsentFeature);
    const emailConsent = !isEmailConsentRequired;

    const birthDate = userPreferences.date || DEFAULT_BIRTH_DATE;
    const rawUrl = metaService.getMeta().href;
    const rawData = [
      {
        birth_date: birthDate,
        isChanged: userInteraction.date.isChanged,
      },
    ];
    const utmParams = { ...utmTags, ...parseSearchParamsFromUrl(new URL(rawUrl)) };

    analyticsService.lead();

    const index = email.indexOf('@');
    const userName = email.slice(0, index);
    const userData: Omit<NewUserDto, 'email'> = {
      type: 'google',
      token,
      name: userName,
      gender: userPreferences.gender || 'female',
      birth_day: birthDate,
      birth_time: null,
      birth_place: 'Greenwich, Greater London, United Kingdom',
      longitude: '0.05',
      latitude: '51.45',
      extra_data: {
        ...userPreferences,
      },
      acquisition_source: quizName,
      acquisition_data: {
        email_consent: emailConsent,
        gender: userPreferences.gender || 'female',
        partnerGender: userPreferences.partnerGender || 'male',
        zodiacSign: zodiac || ZodiacTypes.CAPRICORN,
        partnerZodiacSign: ZodiacTypes.CAPRICORN,
      },
      media_source: utmTags.utm_source,
      seo_channel: utmTags.seo_channel,
      seo_referrer: utmTags.seo_referrer,
      seo_landing_page: utmTags.seo_landing_page,
      quiz: utmTags.quizz,
      campaign_id: utmTags.utm_campaignid,
      campaign: utmTags.utm_campaign,
      adset_id: utmTags.utm_adsetid,
      adset: utmTags.utm_adset,
      ad_id: utmTags.utm_adid,
      ad: utmTags.utm_ad,
      gclid: utmTags.gclid,
      gbraid: utmTags.gbraid,
      fbclid: utmTags.fbclid,
      fb_test_event_code: utmTags.fb_test_event_code,
      sessionId,
      deviceId,
      do_not_sell_data: isOptOutSellUserData,
      ...attributionParams,
      raw_url: rawUrl,
      submitted_at: todayInMilliseconds(),
      utm_params: utmParams,
      raw_data: rawData,
    };

    dispatch(signUpViaGooglePending());

    try {
      const authResult = await authService.signUpViaExternal(userData);

      await crossDomainStorage.saveLastUserEmail(email);
      await analyticsService.setEmailConsent(emailConsent);
      analyticsService.setUserId(String(authResult.user.nebula_id));
      analyticsService.accountCreated(authResult.user.id);
      sessionRecorder.identifyUser(authResult.user.id, {
        email,
        gender: userData.gender,
        acquisition_source: quizName,
      });
      clarityHeatmapRecordingService.addCustomTag('email', email);
      clarityHeatmapRecordingService.addCustomTag('user id', authResult.user.id);
      dispatch(signUpViaGoogleSucceed({ email, id: authResult.user.id }));
    } catch (err) {
      const errorMessage = err instanceof AccountExistError ? 'Account exists' : 'Something went wrong';
      dispatch(signUpViaGoogleFailed(errorMessage));
    }
  };

export const signUpViaFacebook =
  ({ token, quizName, email }: { token: string; quizName: FlowName; email: string }): AppThunk<Promise<void>> =>
  async (dispatch, getState, extra) => {
    const {
      authService,
      analyticsService,
      sessionRecorder,
      crossDomainStorage,
      userService,
      clarityHeatmapRecordingService,
      featureFlags,
      metaService,
    } = extra;

    const state = getState();
    const userPreferences = getUserPreferences(state);
    const zodiac = getZodiac(state);
    const utmTags = getUtmTags(state);
    const userInteraction = getUserInteraction(state);
    const attributionParams = analyticsService.getAttributionParameters();

    const sessionId = await analyticsService.getSessionId();
    const deviceId = await analyticsService.getDeviceId();
    const isOptOutSellUserData = crossDomainStorage.isOptOutSellUserData();

    const isEmailConsentRequired = featureFlags.isFeatureOn(requireEmailConsentFeature);
    const emailConsent = !isEmailConsentRequired;

    const birthDate = userPreferences.date || DEFAULT_BIRTH_DATE;
    const rawUrl = metaService.getMeta().href;
    const rawData = [
      {
        birth_date: birthDate,
        isChanged: userInteraction.date.isChanged,
      },
    ];
    const utmParams = { ...utmTags, ...parseSearchParamsFromUrl(new URL(rawUrl)) };

    analyticsService.lead();

    let userName = 'user' + generateUuid();

    if (email) {
      const index = email.indexOf('@');
      userName = email.slice(0, index);
    }

    const userData: Omit<NewUserDto, 'email'> = {
      type: 'facebook',
      token,
      name: userName,
      gender: userPreferences.gender || 'female',
      birth_day: birthDate,
      birth_time: null,
      birth_place: 'Greenwich, Greater London, United Kingdom',
      longitude: '0.05',
      latitude: '51.45',
      extra_data: {
        ...userPreferences,
      },
      acquisition_source: quizName,
      acquisition_data: {
        email_consent: emailConsent,
        gender: userPreferences.gender || 'female',
        partnerGender: userPreferences.partnerGender || 'male',
        zodiacSign: zodiac || ZodiacTypes.CAPRICORN,
        partnerZodiacSign: ZodiacTypes.CAPRICORN,
      },
      media_source: utmTags.utm_source,
      seo_channel: utmTags.seo_channel,
      seo_referrer: utmTags.seo_referrer,
      seo_landing_page: utmTags.seo_landing_page,
      quiz: utmTags.quizz,
      campaign_id: utmTags.utm_campaignid,
      campaign: utmTags.utm_campaign,
      adset_id: utmTags.utm_adsetid,
      adset: utmTags.utm_adset,
      ad_id: utmTags.utm_adid,
      ad: utmTags.utm_ad,
      gclid: utmTags.gclid,
      gbraid: utmTags.gbraid,
      fbclid: utmTags.fbclid,
      sessionId,
      deviceId,
      do_not_sell_data: isOptOutSellUserData,
      ...attributionParams,
      raw_url: rawUrl,
      submitted_at: todayInMilliseconds(),
      utm_params: utmParams,
      raw_data: rawData,
    };
    dispatch(signUpViaFacebookPending());
    try {
      const authResult = await authService.signUpViaExternal(userData);

      const { email: userEmail } = await userService.getUser();

      let fbEmail = '';

      if (!!userEmail) {
        fbEmail = userEmail.includes('fb-user') ? '' : userEmail;
      }

      await analyticsService.setEmailConsent(emailConsent);
      analyticsService.setUserId(String(authResult.user.nebula_id));
      analyticsService.accountCreated(authResult.user.id);
      sessionRecorder.identifyUser(authResult.user.id, {
        gender: userData.gender,
        acquisition_source: quizName,
      });
      clarityHeatmapRecordingService.addCustomTag('email', email ?? fbEmail);
      clarityHeatmapRecordingService.addCustomTag('user id', authResult.user.id);

      dispatch(
        signUpViaFacebookSucceed({
          email: email ?? fbEmail,
          id: authResult.user.id,
        }),
      );
    } catch (err) {
      const errorMessage = err instanceof AccountExistError ? 'Account exists' : 'Something went wrong';

      dispatch(signUpViaFacebookFailed(errorMessage));
    }
  };

export const signUpViaQuiz =
  ({
    password,
    quizName,
    email,
  }: {
    email: string;
    quizName: FlowName;
    password?: string;
  }): AppThunk<Promise<LoadingStatuses>> =>
  async (dispatch, getState, extra) => {
    const {
      authService,
      analyticsService,
      sessionRecorder,
      crossDomainStorage,
      clarityHeatmapRecordingService,
      featureFlags,
      metaService,
    } = extra;

    const state = getState();
    const userPreferences = getUserPreferences(state);
    const zodiac = getZodiac(state);
    const utmTags = getUtmTags(state);
    const userInteraction = getUserInteraction(state);
    const attributionParams = analyticsService.getAttributionParameters();

    const sessionId = await analyticsService.getSessionId();
    const deviceId = await analyticsService.getDeviceId();
    const isOptOutSellUserData = crossDomainStorage.isOptOutSellUserData();

    const isEmailConsentRequired = featureFlags.isFeatureOn(requireEmailConsentFeature);
    const emailConsent = !isEmailConsentRequired;

    const birthDate = userPreferences.date || DEFAULT_BIRTH_DATE;
    const rawUrl = metaService.getMeta().href;
    const rawData = [
      {
        birth_date: birthDate,
        isChanged: userInteraction.date.isChanged,
      },
    ];
    const utmParams = { ...utmTags, ...parseSearchParamsFromUrl(new URL(rawUrl)) };
    analyticsService.lead();

    const index = email.indexOf('@');
    const userName = email.slice(0, index);

    const userData: NewUserDto = {
      email,
      password,
      name: userPreferences.name || userName,
      gender: userPreferences.gender || 'female',
      birth_day: birthDate,
      birth_time: null,
      birth_place: 'Greenwich, Greater London, United Kingdom',
      longitude: '0.05',
      latitude: '51.45',
      extra_data: {
        ...userPreferences,
      },
      acquisition_source: quizName,
      acquisition_data: {
        email_consent: emailConsent,
        gender: userPreferences.gender || 'female',
        partnerGender: userPreferences.partnerGender || 'male',
        zodiacSign: zodiac || ZodiacTypes.CAPRICORN,
        partnerZodiacSign: ZodiacTypes.CAPRICORN,
      },
      media_source: utmTags.utm_source,
      seo_channel: utmTags.seo_channel,
      seo_referrer: utmTags.seo_referrer,
      seo_landing_page: utmTags.seo_landing_page,
      quiz: utmTags.quizz,
      campaign_id: utmTags.utm_campaignid,
      campaign: utmTags.utm_campaign,
      adset_id: utmTags.utm_adsetid,
      adset: utmTags.utm_adset,
      ad_id: utmTags.utm_adid,
      ad: utmTags.utm_ad,
      gclid: utmTags.gclid,
      gbraid: utmTags.gbraid,
      fbclid: utmTags.fbclid,
      fb_test_event_code: utmTags.fb_test_event_code,
      sessionId,
      deviceId,
      do_not_sell_data: isOptOutSellUserData,
      ...attributionParams,
      raw_url: rawUrl,
      submitted_at: todayInMilliseconds(),
      utm_params: utmParams,
      raw_data: rawData,
    };
    dispatch(signUpViaQuizPending());
    try {
      const authResult = (await authService.signUpViaQuiz(userData)) as EmailDto;

      await crossDomainStorage.saveLastUserEmail(email);
      await analyticsService.setEmailConsent(emailConsent);
      analyticsService.setUserId(String(authResult.user.nebula_id));
      analyticsService.accountCreated(authResult.user.id);
      sessionRecorder.identifyUser(authResult.user.id, {
        email,
        gender: userData.gender,
        acquisition_source: quizName,
      });
      clarityHeatmapRecordingService.addCustomTag('email', email);
      clarityHeatmapRecordingService.addCustomTag('user id', authResult.user.id);

      dispatch(signUpViaQuizSucceed({ email, id: authResult.user.id }));
      return LoadingStatuses.FULFILLED;
    } catch (err) {
      const errorMessage = err instanceof AccountExistError ? 'Account exists' : 'Something went wrong';

      dispatch(signUpViaQuizFailed(errorMessage));
      return LoadingStatuses.FAILED;
    }
  };

export const signIn =
  (credentials: AuthData): AppThunk<Promise<void>> =>
  async (
    dispatch,
    getState,
    { authService, analyticsService, logger, clarityHeatmapRecordingService, metaService },
  ) => {
    const state = getState();
    const utmTags = getUtmTags(state);
    const userPreferences = getUserPreferences(state);
    const userInteraction = getUserInteraction(state);

    const birthDate = userPreferences.date || DEFAULT_BIRTH_DATE;
    const rawUrl = metaService.getMeta().href;
    const rawData = [
      {
        birth_date: birthDate,
        isChanged: userInteraction.date.isChanged,
      },
    ];
    const utmParams = { ...utmTags, ...parseSearchParamsFromUrl(new URL(rawUrl)) };

    try {
      dispatch(signInPending());

      const { user } = await authService.signIn({
        ...credentials,
        raw_url: rawUrl,
        submitted_at: todayInMilliseconds(),
        utm_params: utmParams,
        raw_data: rawData,
      });

      analyticsService.setUserId(String(user.nebula_id));

      dispatch(signInSucceed());
      clarityHeatmapRecordingService.addCustomTag('email', credentials.email);
      clarityHeatmapRecordingService.addCustomTag('user id', user.id);
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);

      dispatch(signInFailed(err.message));

      throw e;
    }
  };

export const requestPasswordReset =
  (data: { email: string }): AppThunk<Promise<void>> =>
  async (dispatch, getState, { authService, logger }) => {
    const { email } = data;

    try {
      dispatch(requestPasswordResetPending());

      await authService.requestPasswordReset(email);

      dispatch(requestPasswordResetSucceed());
    } catch (e) {
      const err = normalizeError(e);
      dispatch(requestPasswordResetFailed(err.message));
      logger.error(err);
    }
  };

export const updateEmail =
  (data: { email: string }): AppThunk<Promise<void>> =>
  async (dispatch, getState, { userService, logger, clarityHeatmapRecordingService }) => {
    const { email } = data;

    try {
      dispatch(updateEmailPending());

      await userService.changeEmail(email);

      dispatch(updateEmailSucceed({ email }));
      clarityHeatmapRecordingService.addCustomTag('email', email);
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);
      dispatch(updateEmailFailed());
    }
  };
